import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Paper,
  Typography,
  Button,
  Avatar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  InputAdornment,
  Skeleton,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import InboxIcon from "@mui/icons-material/Inbox";
import PersonIcon from "@mui/icons-material/Person";
import { toast } from "react-toastify";
import { url } from "../../Components/url";
import { fetchWithAuth } from "../../Components/FetchWithAuth";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import styles from "../AllEnquiries.module.scss";

function StaffPage() {
  const navigate = useNavigate();
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const debouncedSearch = useDebouncedValue(search, 400);

  const loadStaff = async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams({
        page: "1",
        page_size: "100",
        scope: "counsellors",
        sort_by: "first_name",
        sort_dir: "asc",
      });
      const response = await fetchWithAuth(`${url}/get_users?${qs}`, {
        method: "GET",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
      });
      if (response.ok) {
        const data = await response.json();
        const list = Array.isArray(data)
          ? data
          : Array.isArray(data?.users)
            ? data.users
            : [];
        setStaff(list);
      } else {
        const err = await response.json().catch(() => ({}));
        toast.error(err.error || "Failed to load staff");
        setStaff([]);
      }
    } catch (e) {
      toast.error("Failed to load staff");
      setStaff([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadStaff();
  }, []);

  const searchLower = (debouncedSearch || "").trim().toLowerCase();
  const filtered = staff.filter((user) => {
    if (!searchLower) return true;
    const name = `${user?.first_name || ""} ${user?.last_name || ""}`.toLowerCase();
    const email = (user?.email || "").toLowerCase();
    const username = (user?.username || "").toLowerCase();
    return name.includes(searchLower) || email.includes(searchLower) || username.includes(searchLower);
  });

  const handleRowClick = (user) => {
    navigate(`/marketing/counsellor/${user.id}`, { state: { from: "staff" } });
  };

  return (
    <Box className={styles.root}>
      <Paper elevation={0} className={styles.contentPaper}>
            <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 2 }}>
              <Box>
                <Typography variant="h4" className={styles.pageTitle}>
                  Counsellors
                </Typography>
                <Typography variant="body2" className={styles.pageSubtitle} sx={{ mt: 0.5 }}>
                  Users with Counsellor, Visa Counsellor, or {"Visa&Application Counsellor"} roles. Open a profile for
                  revenue and incentives.
                </Typography>
              </Box>
            </Stack>

            <Box sx={{ mb: 3, display: "flex", flexWrap: "wrap", gap: 2, alignItems: "center" }}>
              <TextField
                size="small"
                placeholder="Search by name, email, username…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                sx={{ minWidth: 280 }}
              />
              <Button variant="outlined" size="small" onClick={loadStaff} disabled={loading}>
                Refresh
              </Button>
            </Box>

            {loading && (
              <Box>
                <Skeleton variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
                <Skeleton variant="rectangular" height={400} sx={{ borderRadius: 1 }} />
              </Box>
            )}

            {!loading && filtered.length === 0 && (
              <Stack alignItems="center" justifyContent="center" sx={{ py: 8 }}>
                <InboxIcon sx={{ fontSize: 64, color: "text.secondary", mb: 2 }} />
                <Typography color="text.secondary">No counsellors found</Typography>
              </Stack>
            )}

            {!loading && filtered.length > 0 && (
              <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: 2 }}>
                <Table stickyHeader size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Name</TableCell>
                      <TableCell>Username</TableCell>
                      <TableCell>Email</TableCell>
                      <TableCell>Phone</TableCell>
                      <TableCell>Status</TableCell>
                      <TableCell align="right">Action</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {filtered.map((user) => (
                      <TableRow
                        key={user.id}
                        hover
                        sx={{ cursor: "pointer" }}
                        onClick={() => handleRowClick(user)}
                      >
                        <TableCell>
                          <Stack direction="row" alignItems="center" spacing={1.5}>
                            <Avatar sx={{ width: 36, height: 36, bgcolor: "primary.main" }}>
                              {(user?.first_name || user?.username || "?").charAt(0).toUpperCase()}
                            </Avatar>
                            <Typography variant="body2" fontWeight={500}>
                              {user?.first_name} {user?.last_name}
                            </Typography>
                          </Stack>
                        </TableCell>
                        <TableCell>{user?.username || "—"}</TableCell>
                        <TableCell>{user?.email || "—"}</TableCell>
                        <TableCell>{user?.phone_number || "—"}</TableCell>
                        <TableCell>{user?.current_status || "—"}</TableCell>
                        <TableCell align="right">
                          <Button
                            size="small"
                            variant="outlined"
                            startIcon={<PersonIcon />}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRowClick(user);
                            }}
                          >
                            View profile
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Paper>
    </Box>
  );
}

export default StaffPage;
